import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, CheckCircle, ChevronDown, ArrowRight, Phone } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

// Import real service photos
import imgDriveways from '../assets/images/before-fence.jpg';
import imgHouses from '../assets/images/before-driveway.jpg';
import imgRoof from '../assets/images/service-roof.jpg';
import imgFences from '../assets/images/after-fence.jpg';
import imgPaverInstall from '../assets/images/service-paver-install.jpg';
import imgPaverSealing from '../assets/images/service-paver-sealing.jpg';

const serviceImages = [
  imgDriveways,
  imgHouses,
  imgRoof,
  imgFences,
  imgPaverInstall,
  imgPaverSealing,
];

const ServicesPage = () => {
  const [expandedService, setExpandedService] = useState(null);
  
  // Set expanded service based on hash in URL when page loads
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setExpandedService(hash);
      
      // Scroll to the service after a short delay
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, []);

  const toggleService = (id) => {
    if (expandedService === id) {
      setExpandedService(null);
    } else {
      setExpandedService(id);
    }
  };

  // Services data with detailed information
  const services = [
    { 
      id: 'driveways', 
      title: 'Driveways',
      description: 'Transform your driveway from stained and grimy to clean and inviting. Our professional pressure washing service removes oil stains, tire marks, mold, mildew, and more.',
      longDescription: 'Your driveway is one of the first things people notice about your home. Over time, oil stains, tire marks, dirt, and organic growth can make even the nicest driveway look neglected. Our specialized pressure washing equipment and eco-friendly detergents safely remove these contaminants without damaging your concrete, asphalt, or pavers. We take pride in restoring your driveway to like-new condition, enhancing your home\'s curb appeal and preventing long-term damage to your driveway surface.',
      features: [
        'Removes oil stains and tire marks',
        'Eliminates mold, mildew, and algae',
        'Safe for concrete, asphalt, and pavers',
        'Prevents premature deterioration',
        'Enhances property value and appearance'
      ],
      image: serviceImages[0]
    },
    { 
      id: 'houses', 
      title: 'Houses and Roof Softwashing',
      description: 'Enhance your home\'s curb appeal with our complete exterior house washing service that removes dirt, grime, mildew, and algae from siding, brick, and other surfaces.',
      longDescription: 'Your home\'s exterior faces constant exposure to weather, pollution, and organic growth. Over time, this can lead to a dull, dirty appearance and even cause damage to your siding, trim, and other surfaces. Our house washing service uses a combination of soft washing techniques and appropriate pressure levels to safely clean different exterior materials including vinyl siding, brick, stucco, wood, and more. We carefully protect landscaping and delicate fixtures during the cleaning process to ensure everything except the dirt stays intact.',
      features: [
        'Safe for all exterior surfaces',
        'Removes dirt, grime, mold, and mildew',
        'Prevents damage from organic growth',
        'Extends the life of your paint and siding',
        'Improves air quality around your home',
        'Protects your family from allergens'
      ],
      image: serviceImages[1]
    },
    { 
      id: 'fences', 
      title: 'Fences',
      description: 'Restore your wooden, vinyl, or metal fencing to its original beauty with our specialized fence cleaning services that remove dirt, mold, mildew, and algae.',
      longDescription: 'Fences are constantly exposed to the elements, making them susceptible to dirt buildup, green algae, black mold, and weathering. Our fence cleaning services are tailored to the specific material of your fence—whether wood, vinyl, aluminum, or wrought iron. For wooden fences, we can prepare the surface for staining or sealing after cleaning. Vinyl fences are restored to their bright white appearance, and metal fences have rust and oxidation removed. A clean fence dramatically improves your property\'s appearance and extends the life of the fence materials.',
      features: [
        'Customized cleaning for each fence material',
        'Removes mold, mildew, algae, and dirt',
        'Prepares wooden fences for staining/sealing',
        'Brightens vinyl fencing',
        'Removes rust from metal fencing',
        'Extends fence lifespan'
      ],
      image: serviceImages[3]
    },
    { 
      id: 'paver-installation', 
      title: 'Paver Installation and Sealing',
      description: 'Transform your outdoor living spaces with beautiful, durable paver patios, walkways, and driveways installed by our experienced team.',
      longDescription: 'Pavers offer unmatched beauty, durability, and versatility for outdoor surfaces. Our professional paver installation service includes everything from initial design consultation to final installation. We handle site preparation, base installation, paver placement, and finishing details like edge restraints and joint sand. Choose from a wide variety of paver styles, colors, and patterns to create a unique look that complements your home and landscape. Our proper installation techniques ensure your paver surface will remain beautiful and functional for years to come.',
      features: [
        'Complete design and installation service',
        'Wide selection of paver styles and patterns',
        'Proper base preparation for stability',
        'Professional-grade edge restraints',
        'Polymeric sand for weed prevention',
        'Long-lasting, beautiful results'
      ],
      image: serviceImages[4]
    }
  ];
  
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Services Hero Section */}
        <div className="bg-forest-700 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl font-extrabold text-white sm:text-4xl md:text-5xl">
              Our Professional Services
            </h1>
            <p className="mt-4 text-xl text-forest-100 max-w-3xl mx-auto">
              Comprehensive pressure washing and surface cleaning solutions for your home and property
            </p>
          </div>
        </div>

        {/* Services Navigation */}
        <div className="bg-gray-100 border-b">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-2 md:gap-4">
              {services.map((service) => (
                <a 
                  key={service.id}
                  href={`#${service.id}`}
                  className="px-4 py-2 text-sm md:text-base font-medium text-forest-700 hover:bg-forest-50 rounded-md"
                >
                  {service.title}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Services Content */}
        <div className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-12">
              {services.map((service) => (
                <div 
                  key={service.id} 
                  id={service.id}
                  className="bg-white rounded-lg shadow-lg overflow-hidden"
                >
                  <div className="md:flex">
                    <div className="md:flex-shrink-0 md:w-1/3">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="h-64 w-full object-cover md:h-full"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-6 md:p-8 md:w-2/3">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h2>
                      <p className="text-gray-600 mb-6">{service.description}</p>
                      
                      <button
                        onClick={() => toggleService(service.id)}
                        className="flex items-center text-forest-600 font-medium hover:text-forest-500 focus:outline-none"
                      >
                        {expandedService === service.id ? 'Show Less' : 'Show More'}
                        <ChevronDown 
                          className={`ml-2 h-5 w-5 transform transition-transform ${expandedService === service.id ? 'rotate-180' : ''}`} 
                        />
                      </button>
                      
                      {expandedService === service.id && (
                        <div className="mt-6 border-t border-gray-200 pt-6">
                          <div className="prose max-w-none">
                            <p className="mb-6">{service.longDescription}</p>
                            
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Benefits & Features</h3>
                            <ul className="space-y-2 mb-6">
                              {service.features.map((feature, index) => (
                                <li key={index} className="flex items-start">
                                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                            
                            <div className="mt-6">
                              <Link 
                                to="/contact" 
                                className="inline-flex items-center px-5 py-2 border border-transparent text-base font-medium rounded-md text-white bg-forest-600 hover:bg-forest-500"
                              >
                                Request a Quote
                                <ArrowRight className="ml-2 h-5 w-5" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-forest-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Ready to Get Started?
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Contact us today for a free, no-obligation quote on any of our services.
              We're ready to help restore your property to its original beauty.
            </p>
            <div className="mt-8 flex justify-center">
              <Link 
                to="/contact" 
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-forest-600 hover:bg-forest-500 shadow-md"
              >
                Get Your Free Quote
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
              <a 
                href="tel:+14079063215" 
                className="ml-4 inline-flex items-center px-6 py-3 border border-forest-600 text-base font-medium rounded-md text-forest-600 bg-white hover:bg-forest-50 shadow-md"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;